// MARK: Helper functions to convert RGB strung to Hex
// input: `rgb(r, g, b)`
// output: `#RRGGBB`

export function rgbColorToHex(rgbString) {
  if(rgbString) {
    // Extract the values of r, g, and b from the rgbString
    const [r, g, b] = rgbString.substring(4, rgbString.length-1).split(',').map(val => parseInt(val.trim()));
    
    // Convert the values to hex and concatenate them into a single string
    const hex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
    
    // Prepend the '#' symbol to the hex string and return it
    return '#' + hex;
  }

  return null;
}

export function getContrastWithRGB(r, g, b) {
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  // 128 matches strike-payer's color-contrast.ts and iOS TitleColor, so a brand
  // colour gets the same text colour in the app, on server-rendered tag pages
  // and in the iOS app.
  //
  // This was 160, raised so dark orange (#e67e22, yiq 146.6) took white text.
  // That was the less readable choice: white on it scores 2.85 against WCAG,
  // failing even the 3.0 large-text threshold, where black scores 7.37. Both
  // strike-payer and iOS already chose black. Only colours scoring 128-159 are
  // affected — mid oranges, greys and greens.
  return (yiq >= 128) ? 'light' : 'dark';
}

export function getContrastWithRGBColor(rgbColor) {
  if (!rgbColor) return 'dark';

  let r, g, b;

  if (rgbColor.startsWith('#')) {
    const hex = rgbColor.replace('#', '');
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
  } else {
    const justColors = rgbColor.match(/\d+/g);
    r = (justColors?.length >= 3) ? parseInt(justColors[0], 10) : 0;
    g = (justColors?.length >= 3) ? parseInt(justColors[1], 10) : 0;
    b = (justColors?.length >= 3) ? parseInt(justColors[2], 10) : 0;
  }

  return getContrastWithRGB(r, g, b);
}

export function colorForBackgroundColor(backgroundColor) {
  const isDark = getContrastWithRGBColor(backgroundColor) === 'dark';
  const textColor = isDark ? 'white' : 'black';
  return textColor;
}

export function generateSelectedColor(selectedColor) {
  if (!selectedColor) return null;
  const threshold = 128; // 0x80
  const tolerance = 20; // 0x14
  
  let r, g, b;
  if (selectedColor.startsWith('#')) {
    const hex = selectedColor.replace('#', '');
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
  } else {
    const justColors = selectedColor.match(/\d+/g);
    if (!justColors || justColors.length < 3) return selectedColor;
    r = parseInt(justColors[0], 10);
    g = parseInt(justColors[1], 10);
    b = parseInt(justColors[2], 10);
  }

  // Check if all three numbers are below the threshold (close to black)
  if (r < threshold && g < threshold && b < threshold) {
    // Calculate the absolute differences between each pair of numbers
    const diff1 = Math.abs(r - g);
    const diff2 = Math.abs(r - b);
    const diff3 = Math.abs(g - b);

    // Check if all differences are below a certain tolerance
    if (diff1 < tolerance && diff2 < tolerance && diff3 < tolerance) {
      return null; 
    }
  }

  // Check if all three numbers are very high (close to white)
  if (r > 240 && g > 240 && b > 240) {
    return null;
  }

  // Check if color is the default Strikepay magenta
  if (r === 255 && g === 0 && b === 152) {
    return null;
  }

  return selectedColor;
}

